import { useEffect, useRef, useState } from "react"

/**
 * Whether the element has come into view. Observes once: after the first
 * intersection it disconnects and stays true. Where IntersectionObserver does not
 * exist the element counts as visible.
 *
 * The 200px rootMargin only grows the viewport: inside a scrolling panel
 * (overflow-y-auto) the panel still clips the element, so it counts as visible only
 * once it scrolls into that panel's view.
 */
export function useInViewport<T extends Element>() {
  const ref = useRef<T>(null)
  const [inViewport, setInViewport] = useState(
    typeof IntersectionObserver === "undefined",
  )
  useEffect(() => {
    const node = ref.current
    if (inViewport || !node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInViewport(true)
          observer.disconnect()
        }
      },
      { rootMargin: "200px" },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [inViewport])
  return [ref, inViewport] as const
}
